import { type FC, type ReactNode } from 'react';

import { AccoStars } from '@vakantiesnl/components/src/atoms/AccoStars';
import { Typography } from '@vakantiesnl/components/src/atoms/Typography';
import ImageComponent, { type ImageComponentProps } from '@vakantiesnl/components/src/molecules/ImageComponent';
import { useBreakpoints } from '@vakantiesnl/components/src/utils/useBreakpoints';
import { type Search } from '@vakantiesnl/types';

import { useStyles } from './OfferSummaryTemplate.styles';

export type OfferSummaryTemplateProps = {
	/** Nbc data */
	accommodation: Search.FullAccommodationItem;
	/** Component shown over the acco image */
	imageOverlay?: ReactNode;
	/** Component block shown between the acco title and the flight block */
	topBlock?: ReactNode;
	/** Flight block */
	midBlock?: ReactNode;
	/** Component block shown underneath the flight block */
	bottomBlock?: ReactNode;
};

const TRIM_SIZES: ImageComponentProps['trim'][] = [
	[343, 178],
	[580, 226],
];

export const OfferSummaryTemplate: FC<OfferSummaryTemplateProps> = ({
	accommodation,
	imageOverlay,
	topBlock,
	midBlock,
	bottomBlock,
}) => {
	const { isMobile, isDesktop } = useBreakpoints();
	const { classes } = useStyles();

	return (
		<div className={classes.offerSummaryTemplate}>
			<div className={classes.tripOverviewContainer}>
				<div className={classes.imageContainer}>
					{accommodation.previewImage && (
						<ImageComponent
							alt={accommodation.name}
							src={accommodation.previewImage}
							fill={true}
							trim={isMobile ? TRIM_SIZES[0] : TRIM_SIZES[1]}
							sizes={isDesktop ? '515px' : '100vw'}
							className={classes.image}
						/>
					)}
					{imageOverlay && <div className={classes.overlayContainer}>{imageOverlay}</div>}
				</div>
				<div className={classes.infoContainer}>
					<div className={classes.details}>
						<div className={classes.accommodationTitleWrapper}>
							<Typography
								variant={isDesktop ? 'headingMd' : 'headingSm'}
								as="h4"
								className={classes.accommodationTitle}
								data-cy="OfferSummary-accommodationTitle"
							>
								{accommodation.title}
							</Typography>
							<AccoStars starsCount={accommodation.starRating} size="small" />
						</div>
						{topBlock}
					</div>
					{midBlock && <div className={classes.flights}>{midBlock}</div>}
					<div className={classes.details}>{bottomBlock}</div>
				</div>
			</div>
		</div>
	);
};
